// src/components/ConfirmDialog.jsx
import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const backVariants = { hidden: { opacity: 0 }, show: { opacity: 1 } };
const cardVariants = {
  hidden: { opacity: 0, y: -16, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1 },
};

const ConfirmDialog = ({
  open,
  onCancel,
  onConfirm,
  title = "Are you sure?",
  message = "This action cannot be undone.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  destructive = false,
  icon = null, // optional React node
}) => {
  const confirmBtnRef = useRef(null);

  // ✅ Keyboard shortcuts (no global Enter anymore)
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (!open) return;

      // ESC → close
      if (e.key === "Escape" && !loading) {
        e.preventDefault();
        onCancel?.();
        return;
      }

      // Optional: Y / N shortcuts
      if (!loading && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        onConfirm?.();
        return;
      }

      if (!loading && (e.key === "n" || e.key === "N")) {
        e.preventDefault();
        onCancel?.();
        return;
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onCancel, onConfirm]);

  // Auto-focus (still focusing confirm by default – safe now)
  useEffect(() => {
    if (open && confirmBtnRef.current && !loading) {
      const id = setTimeout(() => confirmBtnRef.current?.focus(), 10);
      return () => clearTimeout(id);
    }
  }, [open, loading]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[1000] flex items-center justify-center"
          initial="hidden"
          animate="show"
          exit="hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            variants={backVariants}
            onClick={() => (!loading ? onCancel?.() : null)}
          />

          {/* Dialog */}
          <motion.div
            className="relative w-full max-w-md mx-3"
            variants={cardVariants}
          >
            <div className="bg-white rounded-xl shadow-2xl border">
              <div className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  {icon ? (
                    <div className="shrink-0">{icon}</div>
                  ) : (
                    <div
                      className={`shrink-0 h-10 w-10 rounded-full grid place-items-center ${
                        destructive
                          ? "bg-rose-100 text-rose-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      !
                    </div>
                  )}

                  <div className="min-w-0">
                    <h3
                      id="confirm-title"
                      className="text-base font-semibold text-slate-800"
                    >
                      {title}
                    </h3>
                    {message ? (
                      <p className="mt-1 text-sm text-slate-600">{message}</p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-5 flex gap-2 justify-end">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={onCancel}
                    className="px-4 py-2 rounded-md border bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {cancelText}
                  </button>

                  <button
                    type="button"
                    ref={confirmBtnRef}
                    disabled={loading}
                    onClick={onConfirm}
                    className={`px-4 py-2 rounded-md text-white disabled:opacity-60 ${
                      destructive
                        ? "bg-rose-600 hover:bg-rose-700"
                        : "bg-indigo-600 hover:bg-indigo-700"
                    }`}
                  >
                    {loading ? "Please wait…" : confirmText}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default ConfirmDialog;
